import React, { useMemo, useState } from 'react';
import {
    NSCard, NSDropdown, NSInput, NSSwitch,
} from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar, faPercentage, faTrash } from '@fortawesome/pro-regular-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import { LOAN_CALCULATION_OPTIONS } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { ISourceProps, SourceField } from './types';

const Source = ({
    source, onChange, onRemove, errors, touched, onBlur, typeOptions,
}: ISourceProps) => {
    const {
        id, name, amount, metricType, calculateDebt, type,
    } = source;

    const [loanToCost, setLoanToCost] = useState((source.loanToCost || 0) * 100);
    const [loanToValue, setLoanToValue] = useState((source.loanToValue || 0) * 100);
    const [debtYield, setDebtYield] = useState((source.debtYield || 0) * 100);

    const selectedType = useMemo(() => typeOptions.find(item => item.value === type), [typeOptions, type]);

    const showAmount = !calculateDebt || (metricType === DEVELOPMENT_SOURCE_METRIC.LTC && calculateDebt);

    const handleInputChange = (id: string, name: SourceField, value: number) => {
        const newValue = Math.min(Math.max(value, 0), 100);
        // all of the values are percentages so we need to divide by 100 to save as decimals in the db
        onChange(id, name, newValue / 100);
    };

    return (
        <NSCard className="p-2 w-100 d-flex flex-row mb-1 align-items-start">
            <div className="flex-grow-1 mx-1">
                <span className="d-flex mr-2 mb-1 text-dark">Name</span>
                <NSInput
                    id="name"
                    type="text"
                    name="name"
                    value={name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(id, 'name', event.target.value)}
                    onBlur={() => onBlur(id, 'name')}
                    showErrorMsg={touched?.name && !!errors?.name}
                    errorMsg={touched?.name ? errors?.name : ''}
                />
            </div>

            <div className="flex-grow-1 mx-1">
                <div className="d-flex flex-direction-row justify-content-between">
                    <span className="mr-2 mb-1 text-dark">Type</span>
                    <RenderIf isTrue={selectedType?.label.includes('Debt')}>
                        <div>
                            <span className="mr-2 mb-1 text-dark">Calculate</span>
                            <NSSwitch
                                id={id}
                                name="calculateSwitch"
                                checked={calculateDebt}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    onChange(id, 'calculateDebt', event.target.checked);
                                }}
                                containerClassName="text-white"
                            />
                        </div>
                    </RenderIf>
                </div>
                <NSDropdown
                    options={typeOptions}
                    selectedOption={selectedType}
                    placeholder="Select type"
                    handleSelectOption={option => onChange(id, 'type', option.value)}
                    containerClassName="mr-2"
                    isFullWidth
                    showErrorMsg={touched?.type && !!errors?.type}
                    errorMsg={touched?.type ? errors?.type : ''}
                />
            </div>

            <RenderIf isTrue={calculateDebt}>
                <div className="flex-grow-0 mx-1">
                    <NSDropdown
                        containerClassName="mb-1"
                        options={LOAN_CALCULATION_OPTIONS}
                        selectedOption={LOAN_CALCULATION_OPTIONS.find(option => option.value === source.metricType) || LOAN_CALCULATION_OPTIONS[0]}
                        handleSelectOption={option => {
                            onChange(id, 'metricType', option.value);
                            onBlur(id, 'metricType');
                        }}
                        customSelectedOptionText={(LOAN_CALCULATION_OPTIONS.find(option => option.value === source.metricType)
                            || LOAN_CALCULATION_OPTIONS[0]).label}
                        badgeColor="dark-lighten"
                        isBadgeToggle
                    />
                    <RenderIf isTrue={metricType === DEVELOPMENT_SOURCE_METRIC.LTC}>
                        <NSInput
                            id="loanToCost"
                            type="number"
                            name="loanToCost"
                            appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                            value={Number(loanToCost.toFixed(2))}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setLoanToCost(Number(event.target.value));
                                handleInputChange(id, 'loanToCost', Number(event.target.value || ''));
                            }}
                            onBlur={() => onBlur(id, 'loanToCost')}
                            showErrorMsg={touched?.loanToCost && !!errors?.loanToCost}
                            errorMsg={touched?.loanToCost ? errors?.loanToCost : ''}
                        />
                    </RenderIf>
                    <RenderIf isTrue={metricType === DEVELOPMENT_SOURCE_METRIC.LTV}>
                        <NSInput
                            id="loanToValue"
                            type="number"
                            name="loanToValue"
                            appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                            value={Number(loanToValue.toFixed(2))}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setLoanToValue(Number(event.target.value));
                                handleInputChange(id, 'loanToValue', Number(event.target.value || ''));
                            }}
                            onBlur={() => onBlur(id, 'loanToValue')}
                            showErrorMsg={touched?.loanToValue && !!errors?.loanToValue}
                            errorMsg={touched?.loanToValue ? errors?.loanToValue : ''}
                        />
                    </RenderIf>
                    <RenderIf isTrue={metricType === DEVELOPMENT_SOURCE_METRIC.DEBT_YIELD}>
                        <NSInput
                            id="debtYield"
                            type="number"
                            name="debtYield"
                            appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                            value={Number(debtYield.toFixed(2))}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setDebtYield(Number(event.target.value));
                                handleInputChange(id, 'debtYield', Number(event.target.value || ''));
                            }}
                            onBlur={() => onBlur(id, 'debtYield')}
                            showErrorMsg={touched?.debtYield && !!errors?.debtYield}
                            errorMsg={touched?.debtYield ? errors?.debtYield : ''}
                        />
                    </RenderIf>
                </div>
            </RenderIf>

            <RenderIf isTrue={showAmount}>
                <div className="flex-grow-1 mx-1">
                    <span className="d-flex mr-2 mb-1 text-dark">Amount</span>
                    <NSInput
                        disabled={calculateDebt && metricType === DEVELOPMENT_SOURCE_METRIC.LTC}
                        id="amount"
                        type="number"
                        name="amount"
                        placeholder="Enter amount"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        value={amount || ''}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(id, 'amount', event.target.value)}
                        onBlur={() => onBlur(id, 'amount')}
                        showErrorMsg={touched?.amount && !!errors?.amount}
                        errorMsg={touched?.amount ? errors?.amount : ''}
                    />
                </div>
            </RenderIf>

            <NSButtonBase icon={faTrash} color="danger" callback={() => onRemove(id)} className="ml-2" outline />
        </NSCard>
    );
};

export default Source;
